import { useMediaQuery } from "@mui/material";
import { useAreYouSure } from "@nvon/react-toolbox";
import { useCallback } from "react";

import { useConsentData } from "@/components/third-party/TruendoCookieConsent/TruendoConsentProvider";
import theme from "../theme";

interface OpenSupportChat {
  openChat: VoidFunction;
}

const supportChatTexts = {
  acceptCookiesTitle: "Cookies akzeptieren",
  acceptCookiesText:
    "Um den Chat zu starten, müssen Sie zunächst die Cookies der Kategorie 'Präferenzen' akzeptieren.",
  acceptCookiesButton: "Cookies akzeptieren & Chat starten",
  braveBlockedTitle: "Ad-Blocker erkannt",
  braveBlockedText:
    "Ihr Browser (z.B. Brave) oder ein Ad-Blocker blockiert unseren Cookie-Consent-Manager. Um den Chat nutzen zu können, deaktivieren Sie bitte den Brave Shield oder Ad-Blocker für diese Website.\n\nBei Brave: Klicken Sie auf das Brave-Logo in der Adresszeile und wählen Sie 'Shields für diese Seite deaktivieren'.\n\nLaden Sie dann die Seite neu.",
  braveBlockedButton: "Verstanden",
};

export const useOpenSupportChat = (): OpenSupportChat => {
  const areYouSure = useAreYouSure();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const { preferences, truendoBlocked } = useConsentData();

  const showBrevoChat = useCallback(() => {
    if (isMobile) {
      window.BrevoConversations("show", true);
    }
    window.BrevoConversations("openChat", true);
  }, [isMobile]);

  /**
   * Accept the cookies preferences
   * and open chat.
   */
  const handleAcceptCookiesAndStartChat = useCallback(() => {
    window.Truendo?.togglePreferences();
    setTimeout(() => {
      if (!window.BrevoConversations) {
        return;
      }
      showBrevoChat();
    }, 1000);
  }, [showBrevoChat]);

  /**
   * Show message when Truendo is blocked by ad blockers
   */
  const showBraveBlockedMessage = useCallback(
    () =>
      areYouSure(() => {}, {
        title: supportChatTexts.braveBlockedTitle,
        content: supportChatTexts.braveBlockedText,
        yesButtonProps: {
          color: "primary",
          children: supportChatTexts.braveBlockedButton,
        },
        noButtonProps: {
          sx: { display: "none" }, // Hide the "No" button
        },
      }),
    [areYouSure],
  );

  /**
   * Cookies need to be accepted first in order to use the chat.
   * This opens a confirmation to accept the cookies.
   */
  const requiredCookiesConfirmation = useCallback(
    () =>
      areYouSure(handleAcceptCookiesAndStartChat, {
        title: supportChatTexts.acceptCookiesTitle,
        content: supportChatTexts.acceptCookiesText,
        yesButtonProps: {
          color: "primary",
          children: supportChatTexts.acceptCookiesButton,
        },
      }),
    [areYouSure, handleAcceptCookiesAndStartChat],
  );

  /**
   * Open chat if cookie preferences
   * have been accepted
   */
  const openChat = useCallback(() => {
    // Check if Truendo is blocked first
    if (truendoBlocked) {
      return showBraveBlockedMessage();
    }

    if (!window.BrevoConversations || !preferences) {
      return requiredCookiesConfirmation();
    }

    showBrevoChat();
  }, [
    preferences,
    truendoBlocked,
    requiredCookiesConfirmation,
    showBrevoChat,
    showBraveBlockedMessage,
  ]);

  return {
    openChat,
  };
};
